import * as colors from "@mui/material/colors";
import { createTheme } from "@mui/material";
import { mergeDeep } from "@parallelpublicworks/clf-utils";
import getFontCss from "./getFontCss";

const defaultTheme = createTheme();

const mui_colors = [
	"red",
	"pink",
	"purple",
	"deepPurple",
	"indigo",
	"blue",
	"lightBlue",
	"cyan",
	"teal",
	"green",
	"lightGreen",
	"lime",
	"yellow",
	"amber",
	"orange",
	"deepOrange",
	"brown",
	"grey",
	"blueGrey",
];

const darkColor = {
	lighter: colors.grey["700"],
	light: colors.grey["800"],
	main: colors.grey["900"],
	dark: colors.grey["900"],
	darker: colors.grey["900"],
	contrastText: defaultTheme.palette.getContrastText(colors.grey["900"]),
};

const lightColor = {
	lighter: "#fff",
	light: colors.grey["100"],
	main: colors.grey["100"],
	dark: colors.grey["200"],
	darker: colors.grey["200"],
	contrastText: defaultTheme.palette.getContrastText(colors.grey["100"]),
};

const greyColor = {
	lighter: colors.grey["400"],
	light: colors.grey["400"],
	main: colors.grey["500"],
	dark: colors.grey["600"],
	darker: colors.grey["600"],
	contrastText: defaultTheme.palette.getContrastText(colors.grey["500"]),
};

const footerColor = { ...darkColor };

defaultTheme.palette.primary = { ...darkColor };
defaultTheme.palette.secondary = { ...greyColor };
defaultTheme.palette.info = { ...lightColor };

const palette = {
	...defaultTheme.palette,
	appBar: {
		private: {
			...defaultTheme.palette.info,
			contrastText: defaultTheme.palette.getContrastText(defaultTheme.palette.info.main),
		},
		public: {
			...defaultTheme.palette.primary,
			contrastText: defaultTheme.palette.getContrastText(
				defaultTheme.palette.primary.main
			),
		},
	},
	titleBar: {
		private: {
			...defaultTheme.palette.info,
			contrastText: defaultTheme.palette.getContrastText(defaultTheme.palette.info.main),
		},
		public: {
			...defaultTheme.palette.info,
			contrastText: defaultTheme.palette.getContrastText(defaultTheme.palette.info.main),
		},
	},
	body: {
		main: colors.grey["100"],
		light: "#f5f5f5",
		lighter: "#ffffff",
		dark: colors.grey["200"],
		contrastText: defaultTheme.palette.getContrastText(colors.grey["100"]),
	},
	footer: footerColor,
	link: {
		...defaultTheme.palette.primary,
		contrastText: defaultTheme.palette.getContrastText(defaultTheme.palette.primary.main),
	},
	dark: darkColor,
	light: lightColor,
};

mui_colors.forEach((color) => {
	const colorObj = colors[color];
	palette[color] = {
		lighter: colorObj["100"],
		light: colorObj["200"],
		main: colorObj["300"],
		dark: colorObj["400"],
		darker: colorObj["500"],
		contrastText: defaultTheme.palette.getContrastText(colorObj["300"]),
	};
});

function getTheme(config) {
	const defaultConfig = {
		breakpoints: {
			keys: ["xxs", "xs", "sm", "md", "lg", "xl", "xxl"],
			values: {
				xxs: 0,
				xs: 600,
				sm: 900,
				md: 1270,
				lg: 1420,
				xl: 1536,
				xxl: 1840,
			},
			unit: "px",
		},
		palette,
		shape: {
			borderRadius: 3,
		},
		typography: {
			fontWeight: 400,
			fontWeightBold: 600,
			fontSizeLg: 18,
			fontSizeSm: 14,
			fontSizeXs: 12,
			h1: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "2.6rem",
			},
			h2: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "2rem",
			},
			h3: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "1.6rem",
			},
			h4: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "1.3rem",
			},
			h5: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "1rem",
			},
			h6: {
				fontWeight: 600,
				lineHeight: 1.167,
				letterSpacing: "-0.01562em",
				fontSize: "0.8rem",
			},
			body2: {
				lineHeight: "1.5",
				opacity: "0.7",
			},
			button: {
				fontWeight: 600,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: (themeParam) => `
          html {
            background-color: ${themeParam.palette.footer.main};
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
          }
          .loading-pulse {
            animation: fadeIn 0.5s infinite alternate linear;
          }
        `,
			},
			MuiTypography: {
				defaultProps: {
					variantMapping: {
						h1: "h1",
						h2: "h2",
						h3: "h3",
						h4: "h4",
						h5: "h4",
						h6: "h4",
						subtitle1: "h4",
						subtitle2: "h4",
						body1: "p",
						body2: "p",
					},
				},
			},
			MuiContainer: {
				styleOverrides: {
					root: ({ ownerState }) => ({
						padding: defaultTheme.spacing(3, 2),
					}),
				},
			},
		},
	};
	config = config ? mergeDeep(defaultConfig, config) : defaultConfig;
	const googleFonts = config.googleFonts ? config.googleFonts : null;

	const muitheme = createTheme(config);

	const fontCss = getFontCss(muitheme);

	muitheme.components.MuiCssBaseline.styleOverrides += ` ${fontCss}`;

	return {
		theme: muitheme,
		googleFonts,
	};
}

export default getTheme;
